import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ITicketFrameStyling,
  TicketFrameComponent,
} from '../ticket-frame/ticket-frame.component';
import {
  ISimpleListItem,
  ISimpleListStyling,
  SimpleListComponent,
} from '../simple-list/simple-list.component';
import { ExtendableTextComponent } from '../../layout/extendable-text/extendable-text.component';
import { ViaListComponent } from '../via-list/via-list.component';
import { ViaChipComponent } from '../via-chip/via-chip.component';
import { ViaBannerComponent } from '../via-banner/via-banner.component';
import { CentsToEurosPipe } from '../../../pipes/cents-to-euros/cents-to-euros.pipe';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { ChipComponent } from '../chip/chip.component';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { ButtonComponent } from '../../feedback/button/button.component';
import {
  IAccessibleImage,
  ICorporateDesign,
  INormalText,
  ticketTypes,
  ViaName,
} from '@web/dtos';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { AppConfigService } from '../../../services/app-config.service';
import { QuillViewComponent } from 'ngx-quill';
import { LanguageService } from '../../../services/language/language.service';

export interface ITicketTileStyling {
  frame: ITicketFrameStyling;
  list: ISimpleListStyling;
}

export type TicketViaBannerType = 'chip' | 'banner' | 'list';

export interface ITicketTile {
  name: string;
  detailsTitle: INormalText[];
  image?: IAccessibleImage;
  details: ISimpleListItem[];
  price?: number;
  afterPriceText: string;
  via: ViaName[];
  description: INormalText[];
  viaBanner: TicketViaBannerType;
  title: INormalText[];
}

@Component({
  selector: 'aw-ticket-tile',
  imports: [
    CommonModule,
    TicketFrameComponent,
    SimpleListComponent,
    ExtendableTextComponent,
    ViaListComponent,
    ViaChipComponent,
    ViaBannerComponent,
    CentsToEurosPipe,
    AppendTestIdPipe,
    ChipComponent,
    LanguagePipe,
    ButtonComponent,
    QuillViewComponent,
  ],
  templateUrl: './ticket-tile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketTileComponent {
  public id: InputSignal<string> = input.required<string>();
  public buttonTextOverride: InputSignal<string | undefined> = input<
    string | undefined
  >();
  public ticket: InputSignal<ITicketTile> = input.required<ITicketTile>();
  public inputStyling: InputSignal<ITicketTileStyling> =
    input<ITicketTileStyling>({
      frame: {
        borderWidth: '1px',
        borderColor: '#C4C4CF',
        backgroundColor: '#fff',
      },
      list: {
        iconColor: '',
      },
    });

  public onButtonClick: OutputEmitterRef<string> = output<string>();

  protected readonly configService: AppConfigService = inject(AppConfigService);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly languagePipe: LanguagePipe = inject(LanguagePipe);
  protected readonly languageService: LanguageService = inject(LanguageService);
  imagePath: Signal<string> = computed((): string => {
    return this.configService.get().general.cmsUrl + '/images/';
  });
  private darkModeStyling: WritableSignal<ITicketTileStyling> = signal({
    frame: {
      borderWidth: '1px',
      borderColor: '#000000',
      backgroundColor:
        this.corporateDesignService.corporateDesign().colorAdmixtures
          .dark_surface_03,
    },
    list: {
      iconColor: '',
    },
  });
  public styling: Signal<ITicketTileStyling> = computed(() => {
    if (this.corporateDesignService.isDarkModeActive()) {
      return this.darkModeStyling();
    }
    return this.inputStyling();
  });
  public corporateDesign: Signal<ICorporateDesign> =
    this.corporateDesignService.corporateDesign;

  protected readonly buttonText: Signal<INormalText[]> =
    this.textStore.buttonText;

  protected readonly descriptionContent: Signal<string> = computed(() =>
    this.languagePipe.transform(this.ticket().description)
  );

  protected readonly descriptionPlainText: Signal<string> = computed(() => {
    const content: string = this.descriptionContent();

    try {
      const delta: unknown = JSON.parse(content);
      if (!this.isQuillDelta(delta)) {
        return content;
      }

      return delta.ops
        .map(operation =>
          typeof operation.insert === 'string' ? operation.insert : ''
        )
        .join('')
        .trim();
    } catch {
      return content;
    }
  });

  private isQuillDelta(
    value: unknown
  ): value is { ops: { insert?: unknown }[] } {
    return (
      typeof value === 'object' &&
      value !== null &&
      'ops' in value &&
      Array.isArray(value.ops)
    );
  }
}
